import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import ShareButton from '../ShareButton';
import { toast } from 'sonner';
import { Copy, Check, ExternalLink, QrCode, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

export function StorefrontLinkBanner({ username }) {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const shareUrl = `zuriya.co.uk/@${username}`;
  const fullUrl = `https://${shareUrl}`;
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch (err) {
      console.error('Clipboard API failed, using fallback:', err);
      const ta = document.createElement('textarea');
      ta.value = fullUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const canvas = document.getElementById('storefront-qr-canvas');
    if (!canvas) return;
    
    const exportCanvas = document.createElement('canvas');
    const padding = 40;
    const textHeight = 60;
    const w = canvas.width + padding * 2;
    const h = canvas.height + padding * 2 + textHeight;
    exportCanvas.width = w;
    exportCanvas.height = h;
    const ctx = exportCanvas.getContext('2d');
    
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(canvas, padding, padding);
    ctx.fillStyle = '#1A1A1A';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(shareUrl, w / 2, canvas.height + padding + 30);
    ctx.fillStyle = '#D4AF37';
    ctx.fillRect(padding, canvas.height + padding + 8, canvas.width, 2);
    
    const link = document.createElement('a');
    link.download = `zuriya-${username}-qr.png`;
    link.href = exportCanvas.toDataURL('image/png');
    link.click();
    toast.success('QR code downloaded!');
  };

  return (
    <>
      <div className="card-surface p-4 mb-6 border-l-4 border-l-[#D4AF37] flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="storefront-link-banner">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#1A1A1A]">Your Storefront</p>
          <p className="text-xs text-stone-500">Share this link with your clients</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <div className="flex items-center gap-0 flex-1 sm:flex-initial">
            <span className="px-3 py-1.5 bg-[#F9F6F0] border border-r-0 border-[#E6E4DD] rounded-l-lg text-sm text-[#D4AF37] font-mono whitespace-nowrap">
              {shareUrl}
            </span>
            <button
              onClick={copyLink}
              className="px-3 py-1.5 border border-[#E6E4DD] rounded-r-lg hover:bg-[#F9F6F0] transition-colors"
              data-testid="copy-storefront-link"
            >
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4 text-stone-500" />}
            </button>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-1"
            onClick={() => setShowQR(true)}
            data-testid="qr-code-btn"
          >
            <QrCode className="h-3.5 w-3.5" />
            QR Code
          </Button>
          <Link to={`/@${username}`} target="_blank">
            <Button variant="outline" size="sm" className="gap-1" data-testid="view-storefront-btn">
              <ExternalLink className="h-3.5 w-3.5" />
              View
            </Button>
          </Link>
          <ShareButton
            path={`/@${username}`}
            title="Check out my services on Zuriya"
          />
        </div>
      </div>

      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-center">Your QR Code</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-5 py-4" data-testid="qr-code-modal">
            <div className="p-5 bg-white rounded-xl shadow-sm border border-[#E6E4DD]">
              <QRCodeCanvas
                id="storefront-qr-canvas"
                value={fullUrl}
                size={220}
                level="H"
                fgColor="#1A1A1A"
                bgColor="#FFFFFF"
                imageSettings={{
                  src: '',
                  height: 0,
                  width: 0,
                  excavate: false,
                }}
              />
            </div>
            <div className="text-center">
              <p className="font-mono text-sm text-[#D4AF37] font-medium">{shareUrl}</p>
              <p className="text-xs text-stone-400 mt-1">Scan to visit your storefront</p>
            </div>
            <div className="flex gap-3 w-full">
              <Button
                onClick={downloadQR}
                className="btn-primary flex-1 gap-2"
                data-testid="download-qr-btn"
              >
                <Download className="h-4 w-4" />
                Download PNG
              </Button>
              <Button
                variant="outline"
                onClick={copyLink}
                className="flex-1 gap-2"
              >
                <Copy className="h-4 w-4" />
                Copy Link
              </Button>
            </div>
            <p className="text-xs text-stone-400 text-center">
              Print this on business cards, flyers, or display at your salon
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
